import {
  fetcher,
  type TCommonMetaProps,
  type TFieldNames,
  type TFields,
  type TMeta,
  type TMetaNames,
} from '@/src/general/utilities/fetcher'
import type { TSignalConfig } from '@/src/general/utilities/fetcher/IFetcher'

import type { TFullTagApi } from '../../tag/api/types/tagApi.types'
import type {
  TCommunityApiHasJoinedInfos,
  TCommunityJoinResponse,
  TJoinCommunityPayload,
  TMediumCommunityApi,
} from './communityApi.types'
import type {
  TGetCommunitiesApiData,
  TGetCommunitiesApiOptionalFields,
  TGetCommunitiesApiPayload,
} from './getCommunities.types'
import type {
  TGetCommunityApiData,
  TGetCommunityApiOptionalAttributes,
  TGetCommunityApiPayload,
} from './getCommunity.types'
import type { TGetSubscriptionsData, TGetSubscriptionsPayload } from './getSubscriptions.types'
import type {
  TGetUserActiveAndVisibleCommunitiesApiData,
  TGetUserActiveAndVisibleCommunitiesApiOptionalFields,
  TGetUserActiveAndVisibleCommunitiesApiPayload,
} from './getUserActiveAndVisibleCommunities.types'

export const SUGGESTION_LIMIT = 10

export const communityApi = {
  getCommunities: <Fields extends TFields<TGetCommunitiesApiOptionalFields>, Meta extends TMeta<TCommonMetaProps>>(
    { limit = 10, offset = 0, ...otherParams }: TGetCommunitiesApiPayload & Fields & Meta,
    { signal }: TSignalConfig,
    tag?: string,
  ) =>
    fetcher.query<TGetCommunitiesApiData<TFieldNames<Fields>, TMetaNames<Meta>>>(
      `/communities${tag ? '/' + tag : ''}`,
      {
        signal,
        params: {
          limit,
          offset,
          ...otherParams,
        },
      },
    ),

  getUserActiveAndVisibleCommunities: <Fields extends TFields<TGetUserActiveAndVisibleCommunitiesApiOptionalFields>>(
    { userId, limit, offset = 0, ...otherParams }: TGetUserActiveAndVisibleCommunitiesApiPayload & Fields,
    { signal }: TSignalConfig,
  ) =>
    fetcher.query<TGetUserActiveAndVisibleCommunitiesApiData<TFieldNames<Fields>>>(
      `/user/${userId}/communities/activeAndVisible`,
      { signal, params: { limit, offset, ...otherParams } },
    ),

  getTag: (tag: string, { signal }: TSignalConfig) =>
    fetcher.query<TFullTagApi>(`/tag/${tag}`, {
      signal,
    }),

  getCommunity: <Fields extends TFields<TGetCommunityApiOptionalAttributes>, Flavor extends boolean | undefined>(
    { id, fields, flavor }: TGetCommunityApiPayload<Flavor> & Fields,
    { signal }: TSignalConfig,
  ) =>
    fetcher.query<TGetCommunityApiData<TFieldNames<Fields>, Flavor>>(`/community/${id}`, {
      params: { fields, flavor },
      signal,
    }),

  leaveCommunity: async (id: number) => fetcher.mutate<void>(`/community/${id}/leave`, {}, { method: 'POST' }),

  incrementCommunityViews: (id: number) => fetcher.mutate<void>(`/community/${id}/view`, {}, { method: 'POST' }),

  joinCommunity: (id: number, body: TJoinCommunityPayload) =>
    fetcher.mutate<TCommunityJoinResponse, TJoinCommunityPayload>(`/community/${id}/join`, body, { method: 'POST' }),

  kickstartSubscription: (id: number) =>
    fetcher.mutate<void>(`/community/${id}/subscription/kickstart`, {}, { method: 'POST' }),

  getUnlivingCommunities: ({ signal }: TSignalConfig) =>
    fetcher.query<(TMediumCommunityApi & TCommunityApiHasJoinedInfos)[]>('/private/communities', {
      signal,
      params: {
        isSubscriptionAlive: false,
      },
    }),

  getSubscriptions: (params: TGetSubscriptionsPayload, { signal }: TSignalConfig) =>
    fetcher.query<TGetSubscriptionsData>('/private/subscriptions', { signal, params }),
}
